import { DocumentTextIcon } from '@heroicons/react/24/outline';
import { strapi } from 'networking';
import { InferGetStaticPropsType } from 'next';
import Image from 'next/image';
import { SVGProps, useEffect, useState } from 'react';
import ReactPlayer from 'react-player/youtube';
import { Staff, StrapiStaffResponse } from 'types';
import hero from '../assets/who-we-are/hero-2-whoweare-min.jpg';
import history from '../assets/who-we-are/history-min.jpg';
import { Button } from '../components/general/button';
import { ClientOnly } from '../components/general/force-client';
import { A } from '../components/general/link';
import { WhiteArch, XSVG } from '../components/img/svgs';
import { Meta } from '../components/meta/meta';
import { GeneralModal } from '../components/modals/general-modal';

export async function getStaticProps() {
  const params = { populate: { image: { fields: ['name', 'url', 'height', 'width'] } } };
  const staffsReq = strapi.get<StrapiStaffResponse>(`/api/staffs`, { params });
  const boardsReq = strapi.get<StrapiStaffResponse>(`/api/boards`, { params });

  const [[staffs], [boards]] = await Promise.all([staffsReq, boardsReq]);

  const staff = staffs.data.map(person => person.attributes).sort((a, b) => a.order - b.order);
  const board = boards.data.map(person => person.attributes).sort((a, b) => a.order - b.order);

  return {
    props: { staff, board },
    revalidate: 21600,
  };
}

export default function WhoWeAre({ staff, board }: InferGetStaticPropsType<typeof getStaticProps>) {
  return (
    <main className="mb-12 lg:mb-0">
      <Meta title="who we are" description="About Beds for Kids and the Beds for Kids team" />
      <section className="relative h-[70vh] min-h-[calc(100vw*550/1477)] w-full overflow-hidden">
        <Image
          alt="Beds for Kids Warehouse"
          src={hero}
          className="absolute z-0 h-full w-full object-cover object-[50%_85%]"
          sizes="100vw"
          priority
        />
        <div className="pointer-events-none absolute -bottom-[1px] left-0 right-0">
          <WhiteArch />
        </div>
      </section>
      <section className="text-blue-500">
        <h1 className="mx-auto my-4 max-w-3xl text-center font-operetta text-6xl">who we are</h1>
        <p className="mx-auto max-w-3xl px-4 text-center font-avenir text-xl leading-6">
          Beds for Kids (EIN 27-4153074) is a Charlotte, NC, based furniture bank. We are a 501(c)3 organization funded
          by individuals, churches and corporations. We are dedicated to serving with dignity and powerfully
          supplementing a family&#39;s resources as they rise out of poverty and into self-sufficiency.
        </p>
        <div className="mx-auto mt-8 grid max-w-5xl items-center justify-center gap-8 px-8 md:grid-cols-3 md:py-8">
          <A
            href="/proof/Beds for Kids 501(c)(3).pdf"
            external
            className="flex h-full max-w-xs items-center rounded-md bg-white p-4 shadow-md hover:shadow-lg">
            <DocumentTextIcon className="h-6 w-6" /> <span className="ml-2">Beds for Kids 501(c)(3) Certification</span>
          </A>
          <A
            href="/proof/2025 990 Beds for Kids Inc.pdf"
            external
            className="flex h-full max-w-xs items-center rounded-md bg-white p-4 shadow-md hover:shadow-lg">
            <DocumentTextIcon className="h-6 w-6" /> <span className="ml-2">Beds for Kids 2025 990</span>
          </A>
          <A
            href="/proof/Case For Support 2026.pdf"
            external
            className="flex h-full max-w-xs items-center rounded-md bg-white p-4 shadow-md hover:shadow-lg">
            <DocumentTextIcon className="h-6 w-6" /> <span className="ml-2">Beds for Kids Case for Support</span>
          </A>
        </div>
        <div style={{ background: `linear-gradient(to bottom, white 80%, #0f004e 80%)` }}>
          <div className="mx-4 mt-12 aspect-[16/9] max-w-3xl lg:mx-auto">
            <ClientOnly>
              <ReactPlayer controls height="100%" width="100%" url={'https://www.youtube.com/embed/QMHlMnvHYKY'} />
            </ClientOnly>
          </div>
        </div>
      </section>
      <section className="bg-blue-500 pt-12 text-white">
        <h2 className="mx-auto mb-12 max-w-3xl px-4 text-center font-caliban text-5xl lg:px-0">
          Beds for Kids mission is to provide beds and essential furniture to children and their families in need.
        </h2>
        <GoalsSection whereDonationsGoButton />
        <WhiteArch className="mt-16" />
      </section>
      <section className="bg-white text-blue-500" id="staff">
        <h2 className="mb-8 mt-4 text-center font-operetta text-5xl lg:mt-0 lg:text-6xl">meet our staff</h2>
        <StaffDisplay staff={staff} />
      </section>
      <section className="mt-20 bg-white text-blue-500" id="board">
        <h2 className="mb-8 mt-4 text-center font-operetta text-5xl lg:mt-0 lg:text-6xl">meet our board</h2>
        <StaffDisplay staff={board} />
      </section>
      <section className="mx-auto mt-24 max-w-6xl bg-white text-blue-500 lg:grid lg:grid-cols-5 lg:gap-12">
        <div className="lg:col-span-2">
          <Image
            src={history}
            alt="Moses loading a truck"
            className="lg:rounded-lg"
            sizes="50vw, (min-width: 768px) 33vw"
          />
        </div>
        <div className="flex h-full flex-col justify-center p-4 text-center lg:col-span-3 lg:p-0 lg:text-left">
          <h2 className="mb-4 font-operetta text-6xl">our history</h2>
          <div className="flex flex-col gap-4 font-avenir text-lg leading-6">
            <p>
              In 2010, a group of individuals led by Daniel Fogarty, Tim Rowley &amp; Brandon Holmes delivered a few
              furniture pieces to a family who had recently moved out of a shelter in Charlotte and into a home.
              Realizing that this family was one of thousands who were moving into housing and were in need of beds and
              other essential furniture, the idea of Beds for Kids was born.
            </p>
            <p>
              In 2011, Beds for Kids officially became a 501(c)3 non-profit. Initially operating out of a 30x10&#39;
              storage unit off South Blvd., Beds for Kids provided 282 beds to children and families in need. Fast
              forward to today, Beds for Kids serves almost ten times that number of beds and essential furniture to our
              client families.
            </p>
            <p>
              In 2021, we relocated from our S. Tryon location to a 17,000 sqft warehouse in order to increase our
              capacity to serve!
            </p>
          </div>
        </div>
      </section>
    </main>
  );
}

function StaffDisplay({ staff }: { staff: Staff[] }) {
  const [modalData, setModalData] = useState<Staff | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!modalData) return;
    setOpen(true);
  }, [modalData]);

  useEffect(() => {
    if (open) return;
    setTimeout(() => setModalData(null), 300);
  }, [open]);

  return (
    <>
      <div className="mx-auto grid max-w-5xl gap-8 md:grid-cols-3">
        {staff.map(s => (
          <button
            key={s.name}
            className="relative flex flex-col items-center px-12 md:px-4"
            onClick={() => setModalData(s)}>
            <Image
              className="aspect-[1/1] w-3/4 overflow-hidden rounded-full object-cover object-[0_20%]"
              alt={s.name}
              sizes="50vw, (min-width: 768px) 33vw"
              height={s.image.data.attributes.height}
              src={s.image.data.attributes.url}
              width={s.image.data.attributes.width}
            />
            <dl className="mt-4 flex flex-col text-center">
              <dt className="font-caliban text-4xl md:text-6xl md:leading-10">{s.name}</dt>
              <dd className="font-avenir text-lg font-semibold leading-6 text-green-500 md:mt-1">{s.role}</dd>
              <div className="mt-2 flex justify-center gap-4">
                <EmailBlueSVG className="h-5 w-5" />
                <BioSVG className="h-5 w-5" />
              </div>
            </dl>
          </button>
        ))}
      </div>
      <GeneralModal
        clean
        open={open}
        onClose={() => setOpen(false)}
        className="mx-4 max-w-5xl overflow-hidden rounded-2xl bg-blue-500">
        <section className="relative md:grid md:grid-cols-5">
          <div className="md:col-span-2">
            <Image
              className="h-full"
              sizes="80vw, (min-width: 768px) 33vw"
              src={modalData?.image.data.attributes.url}
              alt={modalData?.name}
              height={modalData?.image.data.attributes.height}
              width={modalData?.image.data.attributes.width}
            />
          </div>
          <div className="relative flex flex-col justify-center p-8 font-avenir text-white md:col-span-3">
            <div className="text-left">
              <h2 className="font-caliban text-6xl leading-10">{modalData?.name}</h2>
              <p className="mt-1 bg-blue-500 text-lg font-medium text-green-500">{modalData?.role}</p>
              <p className="mt-4 text-xl italic leading-7 text-gray-200 empty:mt-0">
                {modalData?.bio ? `${modalData.bio}` : null}
              </p>
            </div>
            <div className="mt-8 border-t-2 border-green-500 pt-8 md:absolute md:bottom-8 md:left-8 md:right-8 md:mt-0">
              {modalData?.email ? (
                <A external href={`mailto:${modalData?.email}`} className="flex items-center text-lg font-medium">
                  <EmailWhiteSVG className="h-6 w-6" />
                  <span className="ml-2">{modalData?.email}</span>
                </A>
              ) : null}
            </div>
          </div>
          <button className="absolute right-8 top-8" onClick={() => setOpen(false)}>
            <XSVG className="h-6 w-6" />
          </button>
        </section>
      </GeneralModal>
    </>
  );
}

const BioSVG = (props: SVGProps<SVGSVGElement>) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" {...props}>
    <defs>
      <style>{'.bio-icon-1{fill:#0f004e;}'}</style>
    </defs>
    <path
      className="bio-icon-1"
      d="M255.99,511c-3.94,0-7.85-1.5-10.85-4.49l-67.1-67.1H48.99c-26.45,0-47.99-21.54-47.99-47.99V104.82c0-26.45,21.54-47.99,47.99-47.99H463.01c26.45,0,47.99,21.54,47.99,47.99V391.42c0,26.45-21.54,47.99-47.99,47.99h-129.07l-67.1,67.1c-3,3-6.91,4.49-10.85,4.49ZM48.99,87.52c-9.55,0-17.3,7.75-17.3,17.3V391.42c0,9.55,7.75,17.3,17.3,17.3H184.39c4.08,0,7.97,1.62,10.85,4.49l60.75,60.75,60.75-60.75c2.88-2.88,6.77-4.49,10.85-4.49h135.43c9.55,0,17.3-7.75,17.3-17.3V104.82c0-9.55-7.75-17.3-17.3-17.3H48.99Zm346.03,93.71c0-8.47-6.87-15.34-15.34-15.34H132.31c-8.47,0-15.34,6.87-15.34,15.34s6.87,15.34,15.34,15.34h247.36c8.47,0,15.34-6.87,15.34-15.34Zm0,66.88c0-8.47-6.87-15.34-15.34-15.34H132.31c-8.47,0-15.34,6.87-15.34,15.34s6.87,15.34,15.34,15.34h247.36c8.47,0,15.34-6.87,15.34-15.34Zm0,66.88c0-8.47-6.87-15.34-15.34-15.34H132.31c-8.47,0-15.34,6.87-15.34,15.34s6.87,15.34,15.34,15.34h247.36c8.47,0,15.34-6.87,15.34-15.34Z"
    />
  </svg>
);

const EmailBlueSVG = (props: SVGProps<SVGSVGElement>) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" {...props}>
    <defs>
      <style>{'.email-blue-icon-1{fill:#0f004e;}'}</style>
    </defs>
    <g id="_ZwDma">
      <path
        className="email-blue-icon-1"
        d="M511,394.21c.03,4.96-.8,9.9-2.57,14.53-7.73,20.22-24.2,31.2-47.33,31.2-136.48,0-272.96,.01-409.43,0-27.97,0-50.65-22.68-50.66-50.65-.01-93.93-.01-187.86,0-281.79,0-27.97,22.67-50.65,50.64-50.66,58.69-.02,117.37,0,176.06,0,78.13,0,157.82-.02,235.95,.01,14.73,0,27.61,5.48,36.62,14.95,7,7.36,10.67,17.28,10.72,27.43V394.21Zm-29.56-270.32c-2.13,1.78-3.4,2.8-4.63,3.87-68.6,59.45-138.74,118.91-207.35,178.34-9.6,8.31-16.45,8.31-25.93,.09-59.31-51.38-118.6-102.77-177.9-154.16-10.61-9.2-21.24-18.37-32.69-28.28v5.94c0,86.46,0,172.92,0,259.38,0,12.88,6.04,18.95,18.86,18.95,136.34,0,272.68,0,409.02,0,13.2,0,19.07-5.92,19.07-19.24,0-86.29,1.56-172.59,1.56-258.88v-6.01Zm-9.5-34.21H42.91c1.19,1.31,1.49,1.71,1.86,2.03,69.46,60.22,138.95,120.41,208.34,180.71,2.91,2.53,4.47,1.85,6.98-.33,59.62-51.78,120.86-103.48,180.53-155.2,10.13-8.78,20.23-17.58,31.32-27.21Z"
      />
    </g>
  </svg>
);

const EmailWhiteSVG = (props: SVGProps<SVGSVGElement>) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" {...props}>
    <defs>
      <style>{'.email-white-icon-1{fill:#fff;}'}</style>
    </defs>
    <g id="_ZwDma">
      <path
        className="email-white-icon-1"
        d="M511,394.21c.03,4.96-.8,9.9-2.57,14.53-7.73,20.22-24.2,31.2-47.33,31.2-136.48,0-272.96,.01-409.43,0-27.97,0-50.65-22.68-50.66-50.65-.01-93.93-.01-187.86,0-281.79,0-27.97,22.67-50.65,50.64-50.66,58.69-.02,117.37,0,176.06,0,78.13,0,157.82-.02,235.95,.01,14.73,0,27.61,5.48,36.62,14.95,7,7.36,10.67,17.28,10.72,27.43V394.21Zm-29.56-270.32c-2.13,1.78-3.4,2.8-4.63,3.87-68.6,59.45-138.74,118.91-207.35,178.34-9.6,8.31-16.45,8.31-25.93,.09-59.31-51.38-118.6-102.77-177.9-154.16-10.61-9.2-21.24-18.37-32.69-28.28v5.94c0,86.46,0,172.92,0,259.38,0,12.88,6.04,18.95,18.86,18.95,136.34,0,272.68,0,409.02,0,13.2,0,19.07-5.92,19.07-19.24,0-86.29,1.56-172.59,1.56-258.88v-6.01Zm-9.5-34.21H42.91c1.19,1.31,1.49,1.71,1.86,2.03,69.46,60.22,138.95,120.41,208.34,180.71,2.91,2.53,4.47,1.85,6.98-.33,59.62-51.78,120.86-103.48,180.53-155.2,10.13-8.78,20.23-17.58,31.32-27.21Z"
      />
    </g>
  </svg>
);

export function GoalsSection({ whereDonationsGoButton }: { whereDonationsGoButton?: boolean }) {
  return (
    <div className="bg-blue-500 px-4 text-white">
      <h2 className="text-center font-operetta text-5xl lg:text-6xl">
        goals <span className="font-caliban text-6xl font-normal text-green-500 lg:text-7xl">for</span>{' '}
        {new Date().getFullYear()}
      </h2>
      <p className="mx-auto mt-4 max-w-3xl text-center font-avenir text-lg leading-6">
        We strive to serve thousands of children and their families each year.
        <br />
        For {new Date().getFullYear()} we have high ambitions including:
      </p>
      <div className="mx-auto my-8 grid max-w-5xl px-4 font-avenir lg:grid-cols-3">
        <div className="mb-4 border-b-[3px] border-green-500 pb-4 text-center lg:mb-0 lg:border-0 lg:border-r-[3px] lg:pb-0">
          <div className="my-4">
            <p className="text-7xl font-semibold">1,600</p>
            <p className="text-lg">deliveries to families</p>
            <Button link="/furniture" bType="tertiary" className="mt-4 lg:mt-6">
              furniture donations
            </Button>
          </div>
        </div>
        <div className="mb-4 border-b-[3px] border-green-500 pb-4 text-center lg:mb-0 lg:border-0 lg:border-r-[3px] lg:pb-0">
          <div className="my-4">
            <p className="text-7xl font-semibold">$2M</p>
            <p className="text-lg">funds raised</p>
            <Button link="/donate" bType="tertiary" className="mt-4 lg:mt-6">
              monetary donations
            </Button>
          </div>
        </div>
        <div className="text-center">
          <div className="my-4">
            <p className="text-7xl font-semibold">10,000</p>
            <p className="text-lg">volunteer hours given</p>
            <Button link="/how-to-help" bType="tertiary" className="mt-4 lg:mt-6">
              ways to volunteer
            </Button>
          </div>
        </div>
      </div>
      {whereDonationsGoButton ? (
        <div className="text-center">
          <h4 className="text-2xl">Check out how your donations are being used to furnish futures!</h4>
          <Button bType="secondary-white" className="mt-8 text-lg" link="/where-donations-go">
            where our donations go
          </Button>
        </div>
      ) : null}
    </div>
  );
}
